/**
 * Диалог «Переместить оборудование» — Склад ↔ Объекты.
 *
 * План: equipment_mobility_plan.md. Открывается из карточки единицы, только
 * когда сервер вернул mobility.can_move (флаг equipment.move + тумблер
 * организации + переносная подтверждённая единица).
 *
 * Куда: Склады организации отдельной группой сверху (они вне области данных,
 * поэтому приходят своей ручкой /equipment/warehouses), ниже — Объекты из
 * обычного /properties (уже скоуплены сервером). Жилые Объекты не предлагаем:
 * на них модуль не ведётся, сервер ответил бы TARGET_RESIDENTIAL.
 *
 * Отдельный файл, а не компонент внутри EquipmentModal: вложенное объявление
 * пересоздаёт тип на каждом рендере и роняет состояние формы.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { track } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import { usePropertyTypes } from '../hooks/usePropertyTypes';
import BusyLabel from './BusyLabel';

// Коды отказа сервера → ключи i18n. Текст приходит из locales, а не строкой с
// бэка: интерфейс обязан переводиться.
export const MOVE_ERROR_KEYS = {
  NOT_MOBILE: 'equipmentMove.errNotMobile',
  DRAFT: 'equipmentMove.errDraft',
  TARGET_NOT_FOUND: 'equipmentMove.errTarget',
  SAME_PROPERTY: 'equipmentMove.errSame',
  TARGET_RESIDENTIAL: 'equipmentMove.errResidential',
  BAD_DATE: 'equipmentMove.errDate',
  DATE_FUTURE: 'equipmentMove.errFuture',
  DATE_BEFORE_LAST: 'equipmentMove.errBeforeLast',
  CONFLICT: 'equipmentMove.errConflict',
  BULK_REJECTED: 'equipmentMove.errBulk',
  NOT_FOUND: 'equipmentMove.errNotFound',
  NOT_READY: 'equipmentStatus.errNotReady',
  RSA_REQUIRED: 'equipmentMove.errRsaRequired',
  TOO_MANY: 'equipmentMove.errTooMany',
};

/**
 * «Сейчас» в поясе организации в формате поля datetime-local ('YYYY-MM-DDTHH:MM').
 *
 * ⚠️ Пояс организации, а не телефона: бригада в Дубае, офис в Москве, журнал
 * один. Сервер разбирает значение без зоны как местное время организации
 * (services/equipmentMobility.js parseMovedAt) — обе стороны обязаны считать
 * в одном поясе, иначе «сейчас» уезжает на час-другой в будущее и отклоняется.
 */
export function nowInTz(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date()).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch {
    return new Date().toISOString().slice(0, 16);
  }
}

/** Пояс браузера — чтобы подсказать про пояс организации, только если он другой. */
function browserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
}

/**
 * Два режима:
 *   одиночный — `equipment` (карточка единицы): предпросмотр «откуда» и
 *               открытых Работ, POST /:id/move;
 *   массовый  — `bulk = { ids, timezone, onWarehouse }` (список парка Объекта):
 *               POST /move-bulk, всё или ничего; отказ показывает, какие
 *               единицы мешают и почему (фаза 2, equipment_mobility_plan.md).
 */
/**
 * mode — команда из цикла статусов карточки (EquipmentStatusFlow):
 *   'return'            — только Склады; единственный выбирается сам;
 *   'ship' | 'transfer' — только Объекты клиентов;
 *   без mode            — все направления («Другое перемещение», массовое).
 * Проверки те же — сервер не знает про mode, это только сужение списка.
 */
export default function EquipmentMoveDialog({ equipment, bulk, currentPropertyId, mode, onClose, onMoved }) {
  const { t } = useTranslation();
  const { types } = usePropertyTypes();

  const [warehouses, setWarehouses] = useState(null);
  // RSA (договор сервисной аренды) — номер на отгрузке Склад → Объект; обязателен, если включено в настройках орги.
  const [requireRsa, setRequireRsa] = useState(false);
  const [rsa, setRsa] = useState('');
  const [properties, setProperties] = useState(null);
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState('');
  const tz = (bulk ? bulk.timezone : equipment?.mobility?.timezone) || 'Asia/Dubai';
  const fromWarehouse = bulk ? !!bulk.onWarehouse : !!equipment?.mobility?.on_warehouse;
  // Отказ массового перемещения: [{ id, name, code }] — какие единицы мешают.
  const [rejected, setRejected] = useState(null);
  // Отгрузка неготовой (фаза 4): сервер ответил UNREADY_CONFIRM — показываем,
  // что именно не готово, и ждём явного «Отгрузить без готовности».
  // { status } для одной единицы, { units: [{id,name,unit_status}] } для пачки.
  const [unready, setUnready] = useState(null);
  const [initialAt] = useState(() => nowInTz(tz));
  const [date, setDate] = useState(initialAt);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    track('equipment_move_open', { from_warehouse: fromWarehouse, bulk: !!bulk, count: bulk ? bulk.ids.length : 1, mode: mode || 'any' });
    api.equipmentWarehouses().then((r) => { setWarehouses(r.items || []); setRequireRsa(!!r.require_rsa); }).catch(() => setWarehouses([]));
    api.getProperties()
      .then((d) => setProperties(Array.isArray(d) ? d : (d?.properties || [])))
      .catch(() => setProperties([]));
    if (equipment) api.equipmentMovePreview(equipment.id).then(setPreview).catch(() => setPreview(null));
  }, [equipment, bulk, fromWarehouse, mode]);

  // ⚠️ Escape закрывает на keyup, а не keydown: keydown того же нажатия
  // долетел бы до карточки под диалогом и закрыл бы и её (правило модалок
  // проекта, см. CLAUDE.md «Частые грабли»).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) { e.stopPropagation(); onClose(); } };
    window.addEventListener('keyup', onKey);
    return () => window.removeEventListener('keyup', onKey);
  }, [onClose, saving]);

  const typeByKey = useMemo(() => new Map((types || []).map((x) => [x.key, x])), [types]);
  const whIds = useMemo(() => new Set((warehouses || []).map((w) => w.id)), [warehouses]);

  const objects = useMemo(() => (properties || []).filter((p) => {
    if (p.id === currentPropertyId || whIds.has(p.id)) return false;
    const tp = typeByKey.get(String(p.type || '').toLowerCase());
    return !(tp && (tp.is_residential || tp.is_warehouse));
  }), [properties, currentPropertyId, whIds, typeByKey]);

  const whAll = useMemo(() => (warehouses || []).filter((w) => w.id !== currentPropertyId), [warehouses, currentPropertyId]);
  const whOptions = useMemo(() => (mode === 'ship' || mode === 'transfer' ? [] : whAll), [mode, whAll]);
  const objOptions = mode === 'return' ? [] : objects;
  const ready = warehouses !== null && properties !== null;
  // Поле RSA — только на отгрузке со Склада на Объект клиента.
  const showRsa = fromWarehouse && !!target && !whIds.has(Number(target));
  const rsaMissing = showRsa && requireRsa && !rsa.trim();

  // Вариант один — выбираем сразу: «Вернуть на склад» при одном Складе не
  // должен требовать лишнего тапа по списку из одного пункта.
  const only = whOptions.length + objOptions.length === 1 ? (whOptions[0] || objOptions[0]) : null;

  // «Вернуть на склад» при нескольких Складах — по умолчанию тот, с которого
  // единица уехала в последний раз (журнал свежее сверху). Перевозка между
  // клиентами склад не меняет: ищем последнюю отгрузку СО склада, а не
  // последнее перемещение. Не нашли — Склад, где единицу завели. Человек
  // может выбрать другой руками.
  const homeWarehouseId = useMemo(() => {
    if (mode !== 'return' || bulk) return null;
    const allowed = new Set(whOptions.map((w) => w.id));
    const moves = equipment?.mobility?.movements || [];
    const shipped = moves.find((m) => m.from?.is_warehouse && allowed.has(m.from.id));
    if (shipped) return shipped.from.id;
    const start = moves.find((m) => m.kind === 'start' && m.to?.is_warehouse && allowed.has(m.to.id));
    return start ? start.to.id : null;
  }, [mode, bulk, whOptions, equipment]);

  const preselect = only ? only.id : homeWarehouseId;
  useEffect(() => {
    if (ready && mode && preselect && !target) setTarget(String(preselect));
  }, [ready, mode, preselect, target]);

  const submit = async () => {
    if (!target || saving || rsaMissing) return;
    setSaving(true);
    setError(null);
    setRejected(null);
    const confirmUnready = !!unready;
    try {
      const toId = Number(target);
      const payload = { to_property_id: toId, moved_at: date, note: note.trim() || undefined,
        ...(confirmUnready ? { confirm_unready: true } : {}),
        ...(showRsa && rsa.trim() ? { rsa_number: rsa.trim() } : {}) };
      const r = bulk
        ? await api.equipmentMoveBulk({ ...payload, ids: bulk.ids })
        : await api.equipmentMove(equipment.id, payload);
      const toWarehouse = whIds.has(toId);
      track('equipment_moved', {
        source: bulk ? 'bulk' : 'card',
        count: bulk ? (r?.moved || bulk.ids.length) : 1,
        kind: fromWarehouse && !toWarehouse ? 'ship' : !fromWarehouse && toWarehouse ? 'return' : 'transfer',
        backdated: date !== initialAt,
        has_rsa: !!(showRsa && rsa.trim()),
        open_tasks_left: r?.open_tasks || 0,
      });
      if (confirmUnready) track('equipment_dispatch_unready_confirmed', { source: bulk ? 'bulk' : 'card' });
      onMoved(r);
    } catch (err) {
      if (err.code === 'UNREADY_CONFIRM') {
        setUnready(bulk ? { units: err.body?.unready || [] } : { status: err.body?.unit_status });
        return;
      }
      track('equipment_move_failed', { code: err.code || 'unknown', source: bulk ? 'bulk' : 'card' });
      if (err.code === 'BULK_REJECTED') setRejected(err.body?.rejected || []);
      setError(MOVE_ERROR_KEYS[err.code] ? t(MOVE_ERROR_KEYS[err.code]) : (err.message || t('equipmentMove.errGeneric')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} {...backdropClose(() => !saving && onClose())}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{mode ? t(`equipmentStatus.cmd.${mode}`) : t('equipmentMove.title')}</h3>
          <button className="modal-close" onClick={onClose} disabled={saving} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-body">
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
            {bulk
              ? t('equipmentMove.bulkCount', { count: bulk.ids.length })
              : <>{t('equipmentMove.from')}: <b>{preview?.from?.name || '—'}</b></>}
          </div>

          {!ready ? (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
              {t('common.loading')}
            </div>
          ) : (
            <>
              <label className="form-label">{t('equipmentMove.to')}</label>
              <select className="form-input" value={target} data-ph-capture="equipment_move_target"
                      onChange={(e) => { setTarget(e.target.value); setUnready(null); }} style={{ marginBottom: 12 }}>
                <option value="">{t('equipmentMove.pickTarget')}</option>
                {whOptions.length > 0 && (
                  <optgroup label={t('equipmentMove.groupWarehouses')}>
                    {whOptions.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </optgroup>
                )}
                {objOptions.length > 0 && (
                  <optgroup label={t('equipmentMove.groupProperties')}>
                    {objOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {whAll.length === 0 && mode !== 'ship' && mode !== 'transfer' && (
                // Без Склада перемещение между Объектами работает, но возвращать
                // технику некуда — подсказываем, где его завести.
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: -6, marginBottom: 12 }}>
                  {t('equipmentMove.noWarehouses')}
                </div>
              )}

              {showRsa && (
                <>
                  <label className="form-label">
                    {t('equipmentMove.rsa')}{requireRsa ? ' *' : ''}
                  </label>
                  <input type="text" className="form-input" value={rsa} maxLength={100}
                         data-ph-capture="equipment_move_rsa"
                         placeholder={t('equipmentMove.rsaPlaceholder')}
                         onChange={(e) => setRsa(e.target.value)} style={{ marginBottom: 12 }} />
                </>
              )}

              <label className="form-label">{t('equipmentMove.date')}</label>
              <input type="datetime-local" className="form-input" value={date} max={nowInTz(tz)}
                     data-ph-capture="equipment_move_date"
                     onChange={(e) => setDate(e.target.value)}
                     // iOS: «Reset» в шторке не всегда шлёт change — подстраховка
                     // блюром, как у дат в карточке оборудования.
                     onBlur={(e) => { if (e.target.value !== date) setDate(e.target.value); }}
                     style={{ marginBottom: tz !== browserTz() ? 4 : 12 }} />
              {tz !== browserTz() && (
                // Пояс телефона другой — говорим прямо, в каком поясе время,
                // иначе человек в Москве введёт московское для дубайской бригады.
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                  {t('equipmentMove.tzHint', { tz })}
                </div>
              )}

              <label className="form-label">{t('equipmentMove.note')}</label>
              <textarea className="form-input" rows={2} value={note} maxLength={1000}
                        data-ph-capture="equipment_move_note"
                        placeholder={t('equipmentMove.notePlaceholder')}
                        onChange={(e) => setNote(e.target.value)} />

              {preview?.open_tasks > 0 && (
                <div style={{
                  marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                  background: 'rgba(217,119,6,0.10)', border: '1px solid var(--orange)',
                }}>
                  {t('equipmentMove.openTasksWarn', { count: preview.open_tasks })}
                </div>
              )}
            </>
          )}

          {unready && (
            <div style={{
              marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
              background: 'rgba(217,119,6,0.10)', border: '1px solid var(--orange)',
            }}>
              {unready.units
                ? <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('equipmentStatus.unreadyBulk', { count: unready.units.length })}</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                      {unready.units.map((u) => (
                        <li key={u.id}>{u.name || t('equipmentMove.bulkUnknown')} — {t(`equipmentStatus.${u.unit_status}`)}</li>
                      ))}
                    </ul>
                  </>
                : t('equipmentStatus.unreadyOne', { status: unready.status ? t(`equipmentStatus.${unready.status}`) : '—' })}
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>{t('equipmentStatus.unreadyHint')}</div>
            </div>
          )}
          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>}
          {/* Всё или ничего: ни одна единица не переехала. Показываем, какие
              мешают и почему, — снимите их с выбора и повторите. */}
          {rejected && rejected.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--text2)' }}>
              {rejected.map((x) => (
                <li key={x.id}>
                  {x.name || t('equipmentMove.bulkUnknown')} — {t(MOVE_ERROR_KEYS[x.code] || 'equipmentMove.errGeneric')}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose} disabled={saving} data-ph-capture="equipment_move_cancel">
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={!target || saving || !ready || rsaMissing}
                  data-ph-capture="equipment_move_submit">
            <BusyLabel busy={saving} label={t('common.saving')}>
              {unready ? t('equipmentStatus.dispatchAnyway') : t('equipmentMove.submit')}
            </BusyLabel>
          </button>
        </div>
      </div>
    </div>
  );
}
